import {
  Box,
  Chip,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState, useMemo, useEffect } from "react";
import ReusableDataTable from "../../Component/DataTable/ReusableDataTablePrime";
import { FilterMatchMode } from "primereact/api";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../store/CommonSlice";
import { CustomMessage } from "../../const/Spinner";
import { enqueueSnackbar } from "notistack";
import ReusablePrimeButton from "../../Component/Buttons/ReusablePrimeButton";
import AddIcon from "@mui/icons-material/Add";
import { ManufacturerMaster } from "../../types/assetTypes";
import { getManufacturerAssetColumns } from "../../const/AssertData";
import {
  getAssetManufacturerById,
  getManufacturerAssetMasterDataList,
  getModelList,
  saveAssetManufacturer,
} from "../../api/Services/masterServie";
import ReusablePrimeReactDialog from "../../Component/Dialog/ReusablePrimeReactDialog";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { PAGESTART, PAGESEND } from "../../api/endpoint";
import { RootState } from "../../store/Reduxstore";

interface AssetManufacturerProps {
  setPathname: (path: string) => void;
}

const AssetManufacturer: React.FC = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState<ManufacturerMaster[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    rows: 13,
    totalRecords: 0,
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [assetManufacturerData, setAssetManufacturerData] =
    useState<ManufacturerMaster>({
      manufacturerId: 0,
      manufacturerName: "",
      modelIds: []
    });

  // Memoized column definition
  const columns = useMemo(() => getManufacturerAssetColumns(), []);

  if (columns.filter((val) => val.field === "action").length === 0) {
    columns.push({
      field: "action",
      header: "Action",
      sortable: false,
      filter: false,
      filterField: "action",
      filterPlaceholder: "Search",
      style: { minWidth: "12rem" },
      body: (rowData: any) => (
        <ReusablePrimeButton
          icon="pi pi-pencil"
          className="p-button-rounded p-button-outlined"
          onClick={() => handleEditFamilyAsset(rowData.manufacturerId)}
        />
      ),
    });
  }


  const [filterData, setFilterData] = useState<Record<string, any>>(() =>
    (columns ?? []).reduce((acc, col) => {
      if (col.field) {
        acc[col.field] = { value: "", matchMode: "startsWith" }; // Ensure correct structure
      }
      return acc;
    }, {} as Record<string, { value: string; matchMode: string }>),
  );

  const [models, setModels] = useState<{ id: number; name: string }[]>([]);



  const handleEditFamilyAsset = async (
    assetManufacturerAssetManufacturerAssetManufacturerId: number
  ) => {
    dispatch(setLoading(true));
    try {
      const response = await getAssetManufacturerById(
        assetManufacturerAssetManufacturerAssetManufacturerId
      );
      let { data, status } = response;
      if (status.code === 200 && data) {
        setAssetManufacturerData({
          manufacturerId: data.manufacturerId,
          manufacturerName: data.manufacturerName,
          modelIds: data.modelIds
        }
        );
        setOpenDialog(true);
      } else {
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };



  const onFetchData = async (page: number, rows: number, filter?: Record<string, any>) => {
    dispatch(setLoading(true));
    try {
      const { data, status, pagination } =
        await getManufacturerAssetMasterDataList(page, rows, filter ?? filterData);
      if (status.code === 200 && data?.length > 0) {
        setData(data);
        setPagination({
          page: pagination.currentPage,
          rows: pagination.limit,
          totalRecords: pagination.totalCount,
        });
      } else {
        setData([]);
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error?.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    setAssetManufacturerData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClear = () => {
    setOpenDialog(false);
    setAssetManufacturerData({
      manufacturerId: 0,
      manufacturerName: "",
      modelIds: []
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event?.preventDefault();
    if (assetManufacturerData.manufacturerName === "") {
      CustomMessage("Add the name", "error", enqueueSnackbar);
      return;
    }
    dispatch(setLoading(true));
    try {
      const response = await saveAssetManufacturer(assetManufacturerData);
      let { status } = response;
      if (status.code === 200 || status.code === 201) {
        CustomMessage(status.message, "success", enqueueSnackbar);
        handleClear();
        onFetchData(PAGESTART, PAGESEND, filterData);
      } else {
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error?.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    const fetchCountry = async () => {
      try {
        const response = await getModelList(PAGESTART, PAGESEND, {}); // Provide default values
        if (response.status.code === 200) {
          setModels(response.data.map(modelIds => ({
            id: modelIds.modelId,
            name: modelIds.modelName,
          })));

        } else {
          setModels([]);
          CustomMessage(response.status.message, "error", enqueueSnackbar);
        }
      } catch (error: any) {
        CustomMessage(error.message, "error", enqueueSnackbar);
      }
    };

    fetchCountry();
  }, []);

  const userDetails = useSelector((state: RootState) => state.auth)
  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        {userDetails.role.some(
          r => r.roleName === "Admin" || r.roleName === "Assets List Write"
        ) && (
            <Chip
              className="btn-black-outlined mb-3"
              label="New"
              color="warning"
              onClick={() => setOpenDialog(true)}
              icon={<AddIcon />}
              variant="outlined"
            />
          )}
      </div>
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          borderRadius: 3,
          boxShadow: 3,
          padding: 2,
        }}
      >
        <ReusableDataTable
          data={data}
          columns={columns}
          onFetchData={onFetchData}
          emptyMessage="No data found."
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          selectionMode="multiple"
          rowsPerPageOptions={[5, 10, 25, 50]}
          pagination={pagination}
          setPagination={setPagination}
          filters={filterData}
          setFilters={setFilterData}
          onRowDoubleClick={(rowData) => {
            console.log('Double-click row:', rowData);
            handleEditFamilyAsset(rowData.manufacturerId);
          }}
        />
      </Box>
      <ReusablePrimeReactDialog
        header="Add Asset Manufacturer"
        visible={openDialog}
        onHide={handleClear}
        style={{ overflow: 'visible' }}
        contentStyle={{ overflow: 'visible' }}
      >
        <form>
          <div className="mb-3">
            <h6>Asset Manufacturer</h6>
            <TextField
              label=""
              fullWidth
              variant="outlined"
              value={assetManufacturerData.manufacturerName}
              name="manufacturerName"
              onChange={handleChange}
              sx={{ mt: 0 }}
              required
            />
          </div>
          <Box sx={{ mt: 2, mb: 1, fontWeight: "bold", position: "relative" }}>
            <Typography variant="body1" sx={{ mt: 2, mb: 0, fontWeight: "bold" }}>
              Select Model
            </Typography>
            <MultiSelect
              value={assetManufacturerData.modelIds}
              options={models}
              optionLabel="name"
              optionValue="id"
              placeholder="Select Model"
              style={{ width: "100%" }}
              appendTo="self"
              filter
              onChange={(e: MultiSelectChangeEvent) =>
                setAssetManufacturerData((prev) => ({
                  ...prev,
                  modelIds: e.value as number[],
                }))
              }
            />
          </Box>
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: "30px"
          }}>
            <ReusablePrimeButton
              onClick={handleClear}
              severity="contrast"
              title="Cancel"

            >
              Cancel
            </ReusablePrimeButton>
            <ReusablePrimeButton className="ms-1" severity="info" onClick={handleSubmit} allowedRoles={["Admin", "Assets List Write"]}>
              Submit
            </ReusablePrimeButton>
          </div>
        </form>
      </ReusablePrimeReactDialog>
    </>
  );
};

export default AssetManufacturer;
